import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Gamepad2, Gift, Wifi, Zap, Wallet, ShoppingBag, Clock, Award, HeadphonesIcon, ShieldCheck, ZapIcon, CheckCircle2, Star, ChevronRight, QrCode, ChevronDown, FileText, Shield, Cookie, UserCog, AlertTriangle, HelpCircle, TrendingUp, Users, Percent, CreditCard, Package } from 'lucide-react';
import { AppBadge, GlassCard, Button } from '../components/ui';
export const LandingPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  return <div className="w-full" data-id="element-272">
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center pt-20 pb-20 md:pb-32 overflow-hidden" data-id="element-273">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[#020408]" data-id="element-274">
          <div className="absolute inset-0 bg-grid-pattern opacity-[0.03]" data-id="element-275"></div>
          <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-purple-600/20 blur-[150px] rounded-full" data-id="element-276"></div>
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-blue-600/15 blur-[120px] rounded-full" data-id="element-277"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-278">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-279">
            {/* Hero Text */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} className="text-center lg:text-left order-1" data-id="element-280">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.1] mb-6 md:mb-8 tracking-tight" data-id="element-281">
                Level Up Your <br data-id="element-282" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-purple-600 text-glow" data-id="element-283">
                  Gaming
                </span>{' '}
                and <br data-id="element-284" />
                Digital Payments
              </h1>

              <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-8 md:mb-12 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-285">
                Buy CP points, gift cards, airtime, data, bills, and gadgets in
                seconds with one secure app.
              </p>

              {/* Store Badges - Using Uploaded Image */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-6 md:mb-8" data-id="element-286">
                <AppBadge platform="ios" data-id="element-287" />
                <AppBadge platform="android" data-id="element-288" />
              </div>

              <div className="mb-8 md:mb-12" data-id="element-289">
                <Link to="/terms" className="text-sm text-gray-400 hover:text-purple-400 underline underline-offset-4 transition-colors" data-id="element-290">
                  Read Terms and Privacy
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-291">
                {[{
                icon: ZapIcon,
                text: 'Instant delivery',
                color: 'text-yellow-400'
              }, {
                icon: ShieldCheck,
                text: 'Secure wallet',
                color: 'text-green-400'
              }, {
                icon: CheckCircle2,
                text: 'Trusted payments',
                color: 'text-blue-400'
              }, {
                icon: HeadphonesIcon,
                text: '24/7 support',
                color: 'text-purple-400'
              }].map((item, i) => <div key={i} className="flex flex-col items-center gap-2 text-center" data-id="element-292">
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center ${item.color}`} data-id="element-293">
                      <item.icon className="w-5 h-5 md:w-6 md:h-6" data-id="element-294" />
                    </div>
                    <span className="text-xs md:text-sm font-medium text-gray-300" data-id="element-295">
                      {item.text}
                    </span>
                  </div>)}
              </div>
            </motion.div>

            {/* 3D Double Phone Mockup - VISIBLE ON MOBILE */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 0.2
          }} className="relative h-[500px] sm:h-[600px] lg:h-[700px] flex items-center justify-center order-2" data-id="element-296">
              {/* Glow Effect */}
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-297">
                <div className="w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] bg-purple-500/30 blur-[100px] rounded-full" data-id="element-298"></div>
              </div>

              {/* Back Phone - Marketplace */}
              <div className="absolute top-8 sm:top-12 right-4 sm:right-8 lg:right-12 z-10 transform rotate-6 hover:rotate-3 transition-transform duration-500" data-id="element-299">
                <div className="w-[200px] h-[420px] sm:w-[240px] sm:h-[500px] lg:w-[280px] lg:h-[580px] rounded-[32px] sm:rounded-[40px] border-[6px] sm:border-[8px] border-gray-900 bg-black shadow-2xl shadow-blue-500/20 overflow-hidden" data-id="element-300">
                  <div className="absolute top-0 inset-x-0 h-6 sm:h-8 flex justify-center z-20" data-id="element-301">
                    <div className="w-24 sm:w-32 h-5 sm:h-7 bg-black rounded-b-2xl sm:rounded-b-3xl" data-id="element-302"></div>
                  </div>
                  <img src="https://cdn.magicpatterns.com/uploads/szSm9qFQ9sguuZJsRcKuWe/WhatsApp_Image_2026-03-25_at_11.59.00_AM.jpg" alt="KDX Marketplace" className="w-full h-full object-cover" data-id="element-303" />
                </div>
              </div>

              {/* Front Phone - Home Dashboard */}
              <div className="absolute top-0 left-4 sm:left-8 lg:left-12 z-20 transform -rotate-3 hover:rotate-0 transition-transform duration-500 shadow-2xl shadow-purple-500/40" data-id="element-304">
                <div className="w-[220px] h-[460px] sm:w-[260px] sm:h-[540px] lg:w-[300px] lg:h-[620px] rounded-[32px] sm:rounded-[40px] border-[6px] sm:border-[8px] border-gray-900 bg-black overflow-hidden" data-id="element-305">
                  <div className="absolute top-0 inset-x-0 h-6 sm:h-8 flex justify-center z-20" data-id="element-306">
                    <div className="w-24 sm:w-32 h-5 sm:h-7 bg-black rounded-b-2xl sm:rounded-b-3xl" data-id="element-307"></div>
                  </div>
                  <img src="https://cdn.magicpatterns.com/uploads/fGdv4occVWTXBJUZM3LhsK/WhatsApp_Image_2026-03-25_at_11.05.08_AM_(1).jpg" alt="KDX Home Dashboard" className="w-full h-full object-cover" data-id="element-308" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURED BRANDS */}
      <section className="py-12 md:py-16 border-y border-white/5 bg-white/[0.02] relative overflow-hidden" data-id="element-309">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/5 via-transparent to-blue-900/5" data-id="element-310"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-311">
          <p className="text-center text-xs text-gray-500 font-semibold mb-8 md:mb-10 uppercase tracking-widest" data-id="element-312">
            Trusted by gamers for
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 lg:gap-20" data-id="element-313">
            {['COD Mobile', 'eFootball', 'PUBG', 'Steam', 'Google Play', 'Apple', 'MTN', 'DSTV'].map(brand => <div key={brand} className="text-lg md:text-xl lg:text-2xl font-bold text-white/40 hover:text-white/80 transition-all duration-300 tracking-wide" data-id="element-314">
                {brand}
              </div>)}
          </div>
        </div>
      </section>

      {/* SEE KDX IN ACTION */}
      <section className="py-20 md:py-32 bg-[#020408] relative overflow-hidden" data-id="element-315">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-purple-900/10 to-transparent" data-id="element-316"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-317">
          <div className="text-center mb-12 md:mb-20" data-id="element-318">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 md:mb-6" data-id="element-319">
              See KDX in Action
            </h2>
            <p className="text-lg md:text-xl text-gray-400" data-id="element-320">
              A premium interface designed for the modern gamer
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 max-w-6xl mx-auto" data-id="element-321">
            {[{
            name: 'Home Dashboard',
            img: 'https://cdn.magicpatterns.com/uploads/fGdv4occVWTXBJUZM3LhsK/WhatsApp_Image_2026-03-25_at_11.05.08_AM_(1).jpg',
            desc: 'Quick access to all services'
          }, {
            name: 'Marketplace',
            img: 'https://cdn.magicpatterns.com/uploads/szSm9qFQ9sguuZJsRcKuWe/WhatsApp_Image_2026-03-25_at_11.59.00_AM.jpg',
            desc: 'Shop games and gadgets'
          }, {
            name: 'Recent Transactions',
            img: 'https://cdn.magicpatterns.com/uploads/qKQzuqsLogyuE6LwtcSqQF/WhatsApp_Image_2026-03-25_at_11.25.10_AM.jpg',
            desc: 'Track your purchases'
          }].map((screen, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.2
          }} viewport={{
            once: true
          }} className="flex flex-col items-center gap-6" data-id="element-322">
                <div className="relative group" data-id="element-323">
                  <div className="w-[240px] h-[500px] sm:w-[260px] sm:h-[540px] md:w-[280px] md:h-[580px] rounded-[36px] md:rounded-[40px] border-[7px] md:border-[8px] border-gray-900 bg-black shadow-2xl overflow-hidden group-hover:scale-105 transition-transform duration-500" data-id="element-324">
                    <div className="absolute top-0 inset-x-0 h-7 md:h-8 flex justify-center z-20" data-id="element-325">
                      <div className="w-28 md:w-32 h-6 md:h-7 bg-black rounded-b-3xl" data-id="element-326"></div>
                    </div>
                    <img src={screen.img} alt={screen.name} className="w-full h-full object-cover" data-id="element-327" />
                  </div>
                  <div className="absolute inset-0 bg-purple-500/20 blur-[60px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-328"></div>
                </div>
                <div className="text-center" data-id="element-329">
                  <h3 className="text-lg md:text-xl font-bold text-white mb-2" data-id="element-330">
                    {screen.name}
                  </h3>
                  <p className="text-sm text-gray-400" data-id="element-331">{screen.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CORE FEATURES */}
      <section id="features" className="py-20 md:py-32 relative" data-id="element-332">
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" data-id="element-333"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-334">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20" data-id="element-335">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 md:mb-6" data-id="element-336">
              Everything You Need in One App
            </h2>
            <p className="text-lg md:text-xl text-gray-400" data-id="element-337">
              A premium ecosystem designed for gamers and digital natives.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-338">
            {[{
            icon: Gamepad2,
            title: 'Game Top-Up',
            desc: 'Top up COD Mobile, eFootball, PUBG, FC Mobile, and more instantly.',
            color: 'from-purple-500/20 to-purple-600/20',
            iconColor: 'text-purple-400'
          }, {
            icon: Gift,
            title: 'Gift Cards',
            desc: 'Buy digital gift cards like Steam, Google Play, Apple, and Amazon.',
            color: 'from-pink-500/20 to-pink-600/20',
            iconColor: 'text-pink-400'
          }, {
            icon: Wifi,
            title: 'Airtime and Data',
            desc: 'Recharge MTN, Glo, Airtel, and more in seconds.',
            color: 'from-blue-500/20 to-blue-600/20',
            iconColor: 'text-blue-400'
          }, {
            icon: Zap,
            title: 'Cable TV & Electricity',
            desc: 'Pay for DSTV, GOtv, StarTimes, PHED, EEDC, EKEDC, and more.',
            color: 'from-yellow-500/20 to-yellow-600/20',
            iconColor: 'text-yellow-400'
          }, {
            icon: Wallet,
            title: 'Wallet',
            desc: 'Fund your wallet, manage balance, and complete purchases faster.',
            color: 'from-green-500/20 to-green-600/20',
            iconColor: 'text-green-400'
          }, {
            icon: ShoppingBag,
            title: 'Marketplace',
            desc: 'Shop gadgets, gaming accessories, and selected digital products.',
            color: 'from-orange-500/20 to-orange-600/20',
            iconColor: 'text-orange-400'
          }, {
            icon: Clock,
            title: 'Orders & Tracking',
            desc: 'Track purchases, order status, and recent transactions.',
            color: 'from-cyan-500/20 to-cyan-600/20',
            iconColor: 'text-cyan-400'
          }, {
            icon: Award,
            title: 'Rewards',
            desc: 'Earn points, cashback, promo rewards, or loyalty benefits.',
            color: 'from-rose-500/20 to-rose-600/20',
            iconColor: 'text-rose-400'
          }, {
            icon: HeadphonesIcon,
            title: 'Support',
            desc: 'Get help through live chat, help center, or support tickets.',
            color: 'from-indigo-500/20 to-indigo-600/20',
            iconColor: 'text-indigo-400'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} viewport={{
            once: true
          }} data-id="element-339">
                <GlassCard className={`group cursor-pointer h-full bg-gradient-to-br ${feature.color} border-white/10 hover:border-purple-500/30`} data-id="element-340">
                  <div className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(139,92,246,0.3)] transition-all duration-300`} data-id="element-341">
                    <feature.icon className={`w-6 h-6 md:w-7 md:h-7 ${feature.iconColor}`} data-id="element-342" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4" data-id="element-343">
                    {feature.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-400 leading-relaxed" data-id="element-344">
                    {feature.desc}
                  </p>
                </GlassCard>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-32 bg-[#020408] relative overflow-hidden" data-id="element-345">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/5 to-transparent" data-id="element-346"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-347">
          <div className="text-center mb-12 md:mb-20" data-id="element-348">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 md:mb-6" data-id="element-349">
              How It Works
            </h2>
            <p className="text-lg md:text-xl text-gray-400" data-id="element-350">
              Get started in 4 simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 relative" data-id="element-351">
            <div className="hidden lg:block absolute top-16 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" data-id="element-352"></div>

            {[{
            step: '01',
            title: 'Create Account',
            desc: 'Download the app and sign up in seconds with your email or phone number.',
            icon: Users
          }, {
            step: '02',
            title: 'Fund Wallet',
            desc: 'Add money using cards, bank transfer, or mobile money.',
            icon: CreditCard
          }, {
            step: '03',
            title: 'Select Service',
            desc: 'Choose from games, gift cards, bills, or shop the marketplace.',
            icon: Package
          }, {
            step: '04',
            title: 'Instant Delivery',
            desc: 'Complete payment and receive your items instantly.',
            icon: ZapIcon
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.15
          }} viewport={{
            once: true
          }} className="relative" data-id="element-353">
                <div className="flex flex-col items-center text-center" data-id="element-354">
                  <div className="relative mb-6 md:mb-8" data-id="element-355">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-xl md:text-2xl font-bold text-white shadow-[0_0_30px_rgba(139,92,246,0.5)] relative z-10" data-id="element-356">
                      {item.step}
                    </div>
                    <div className="absolute inset-0 bg-purple-500/30 blur-xl rounded-full" data-id="element-357"></div>
                  </div>
                  <item.icon className="w-7 h-7 md:w-8 md:h-8 text-purple-400 mb-3 md:mb-4" data-id="element-358" />
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3" data-id="element-359">
                    {item.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-400" data-id="element-360">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE KDX */}
      <section className="py-20 md:py-32 relative" data-id="element-361">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-362">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center" data-id="element-363">
            <div data-id="element-364">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-6 md:mb-8" data-id="element-365">
                Why Choose KDX
              </h2>
              <p className="text-lg md:text-xl text-gray-400 mb-8 md:mb-12" data-id="element-366">
                We built KDX because gamers deserve a platform that is as fast
                and reliable as their reflexes.
              </p>

              <div className="space-y-4 md:space-y-6" data-id="element-367">
                {['Lightning-fast checkout and delivery', 'Bank-grade security and encryption', 'Gaming and utility bills in one place', 'Clean transaction history and tracking', 'Trusted 24/7 customer support', 'Premium user experience'].map((point, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.1
              }} viewport={{
                once: true
              }} className="flex items-center gap-4 md:gap-5 glass-panel p-4 md:p-5 rounded-2xl group hover:bg-white/[0.08] transition-all" data-id="element-368">
                    <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-purple-500/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform" data-id="element-369">
                      <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-purple-400" data-id="element-370" />
                    </div>
                    <span className="text-base md:text-lg text-white font-medium" data-id="element-371">
                      {point}
                    </span>
                  </motion.div>)}
              </div>
            </div>

            <div className="relative" data-id="element-372">
              <div className="absolute inset-0 bg-blue-500/20 blur-[120px] rounded-full" data-id="element-373"></div>
              <GlassCard className="relative p-8 md:p-10 border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-blue-900/20" data-id="element-374">
                <div className="flex items-center justify-between mb-8 md:mb-10 pb-6 md:pb-8 border-b border-white/10" data-id="element-375">
                  <span className="text-xl md:text-2xl font-bold text-white" data-id="element-376">
                    The KDX Advantage
                  </span>
                  <Award className="w-8 h-8 md:w-10 md:h-10 text-purple-400" data-id="element-377" />
                </div>

                <div className="space-y-6 md:space-y-8" data-id="element-378">
                  {[{
                  label: 'Transaction Speed',
                  value: '< 3 seconds',
                  color: 'text-green-400'
                }, {
                  label: 'Hidden Fees',
                  value: 'Zero',
                  color: 'text-blue-400'
                }, {
                  label: 'Support Availability',
                  value: '24/7 Live',
                  color: 'text-purple-400'
                }, {
                  label: 'Security Level',
                  value: 'Bank-Grade',
                  color: 'text-yellow-400'
                }].map((stat, i) => <div key={i} className="flex justify-between items-center" data-id="element-379">
                      <span className="text-gray-400 text-base md:text-lg" data-id="element-380">
                        {stat.label}
                      </span>
                      <span className={`text-lg md:text-xl font-bold ${stat.color}`} data-id="element-381">
                        {stat.value}
                      </span>
                    </div>)}
                </div>
              </GlassCard>
            </div>
          </div>
        </div>
      </section>

      {/* REWARDS */}
      <section id="rewards" className="py-20 md:py-32 bg-[#020408] relative overflow-hidden" data-id="element-382">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-pink-600/10 blur-[150px] rounded-full" data-id="element-383"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-384">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20" data-id="element-385">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 md:mb-6" data-id="element-386">
              Rewards That Keep You Playing
            </h2>
            <p className="text-lg md:text-xl text-gray-400" data-id="element-387">
              Enjoy bonuses, loyalty perks, promo discounts, cashback, and
              special campaign benefits.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 mb-12 md:mb-16" data-id="element-388">
            {[{
            title: 'Welcome Bonus',
            icon: Gift,
            color: 'from-pink-500/20 to-pink-600/20',
            iconColor: 'text-pink-400'
          }, {
            title: 'Referral Rewards',
            icon: Users,
            color: 'from-blue-500/20 to-blue-600/20',
            iconColor: 'text-blue-400'
          }, {
            title: 'Promo Codes',
            icon: Percent,
            color: 'from-purple-500/20 to-purple-600/20',
            iconColor: 'text-purple-400'
          }, {
            title: 'Seasonal Deals',
            icon: Zap,
            color: 'from-yellow-500/20 to-yellow-600/20',
            iconColor: 'text-yellow-400'
          }, {
            title: 'Cashback',
            icon: TrendingUp,
            color: 'from-green-500/20 to-green-600/20',
            iconColor: 'text-green-400'
          }].map((reward, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: i * 0.1
          }} viewport={{
            once: true
          }} data-id="element-389">
                <GlassCard className={`text-center p-6 md:p-8 flex flex-col items-center justify-center gap-4 md:gap-5 h-full bg-gradient-to-br ${reward.color} border-white/10 hover:border-purple-500/30 cursor-pointer`} data-id="element-390">
                  <reward.icon className={`w-8 h-8 md:w-10 md:h-10 ${reward.iconColor}`} data-id="element-391" />
                  <span className="text-sm md:text-base font-bold text-white" data-id="element-392">
                    {reward.title}
                  </span>
                </GlassCard>
              </motion.div>)}
          </div>

          <div className="glass-panel p-5 md:p-6 rounded-2xl border-white/5 text-center max-w-4xl mx-auto" data-id="element-393">
            <p className="text-xs md:text-sm text-gray-500" data-id="element-394">
              Rewards, promotions, bonus credits, and discounts are subject to
              campaign rules, eligibility, account verification, abuse
              prevention checks, and availability.{' '}
              <Link to="/rewards-rules" className="text-purple-400 hover:underline font-medium" data-id="element-395">
                Full reward rules apply.
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* MARKETPLACE PREVIEW */}
      <section id="marketplace" className="py-20 md:py-32 relative" data-id="element-396">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-397">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 md:mb-16 gap-6" data-id="element-398">
            <div data-id="element-399">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 md:mb-6" data-id="element-400">
                More Than Just Top-Ups
              </h2>
              <p className="text-lg md:text-xl text-gray-400 max-w-2xl" data-id="element-401">
                Shop premium gaming gadgets, accessories, and exclusive digital
                bundles directly in the app.
              </p>
            </div>
            <Button variant="outline" className="shrink-0 text-base md:text-lg px-6 md:px-8 py-3 md:py-4" data-id="element-402">
              View All Products{' '}
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5" data-id="element-403" />
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-id="element-404">
            {[{
            name: 'iPhone',
            price: 'From ₦850,000',
            img: 'https://cdn.magicpatterns.com/uploads/bRNLgcvU2GsoBShxec1L63/08f487b14d2cce868990e9022230ce4b.jpg',
            badge: 'Premium'
          }, {
            name: 'Holy Stone Drone',
            price: '₦120,000',
            img: 'https://cdn.magicpatterns.com/uploads/okbEXfo4xGjC73dn4dWrVe/488da38d0305635ceab8a5fd8be12434.jpg',
            badge: 'Hot'
          }, {
            name: 'MSI Gaming Laptop',
            price: '₦1,250,000',
            img: 'https://cdn.magicpatterns.com/uploads/usjFpvLbrAEJJviBwm3Hsw/WebB14WFK-287MY1.webp',
            badge: 'Premium'
          }, {
            name: 'PlayStation Backbone',
            price: '₦85,000',
            img: 'https://cdn.magicpatterns.com/uploads/tEmPTnsMkyRWzuUcSQp5Si/BACKBONE-A.webp',
            badge: 'New'
          }].map((product, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} viewport={{
            once: true
          }} data-id="element-405">
                <GlassCard className="p-0 flex flex-col h-full group overflow-hidden border-white/10 hover:border-purple-500/30 transition-all duration-300 hover:shadow-[0_0_30px_rgba(139,92,246,0.3)]" data-id="element-406">
                  <div className="h-48 md:h-64 relative overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800" data-id="element-407">
                    {product.badge && <div className="absolute top-4 right-4 z-10 bg-purple-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg" data-id="element-408">
                        {product.badge}
                      </div>}
                    <img src={product.img} alt={product.name} className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500" data-id="element-409" />
                  </div>
                  <div className="p-5 md:p-6 flex-1 flex flex-col justify-between" data-id="element-410">
                    <div data-id="element-411">
                      <h3 className="text-lg md:text-xl font-bold text-white mb-2" data-id="element-412">
                        {product.name}
                      </h3>
                      <p className="text-xl md:text-2xl text-purple-400 font-bold mb-4 md:mb-6" data-id="element-413">
                        {product.price}
                      </p>
                    </div>
                    <Button variant="secondary" className="w-full group-hover:bg-purple-600 group-hover:text-white transition-colors" data-id="element-414">
                      Buy Now
                    </Button>
                  </div>
                </GlassCard>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-32 bg-[#020408]" data-id="element-415">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-416">
          <div className="text-center mb-12 md:mb-20" data-id="element-417">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 md:mb-6" data-id="element-418">
              Trusted by Gamers
            </h2>
            <div className="flex items-center justify-center gap-2 text-yellow-400 mb-3 md:mb-4" data-id="element-419">
              {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-5 h-5 md:w-6 md:h-6 fill-current" data-id="element-420" />)}
            </div>
            <p className="text-xl md:text-2xl text-gray-400 font-semibold" data-id="element-421">
              4.9/5 Average Rating
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-422">
            {[{
            text: "Fast top-up and clean interface. I bought COD points in minutes and they reflected instantly. Best gaming payment app I've used.",
            author: 'Michael T.',
            role: 'Pro Gamer'
          }, {
            text: "The wallet feature makes repeat purchases so easy. I don't have to enter my card details every time. Super convenient!",
            author: 'Sarah K.',
            role: 'Mobile Gamer'
          }, {
            text: 'I like having games, gift cards, and bill payments in one app. Saves me from downloading 5 different apps. Highly recommend!',
            author: 'David O.',
            role: 'Content Creator'
          }].map((review, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.15
          }} viewport={{
            once: true
          }} data-id="element-423">
                <GlassCard className="p-8 md:p-10 h-full flex flex-col border-white/10 hover:border-purple-500/20" data-id="element-424">
                  <div className="flex gap-1 text-yellow-400 mb-5 md:mb-6" data-id="element-425">
                    {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 md:w-5 md:h-5 fill-current" data-id="element-426" />)}
                  </div>
                  <p className="text-gray-300 mb-6 md:mb-8 italic text-base md:text-lg leading-relaxed flex-1" data-id="element-427">
                    "{review.text}"
                  </p>
                  <div className="flex items-center gap-3 md:gap-4" data-id="element-428">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg md:text-xl" data-id="element-429">
                      {review.author.charAt(0)}
                    </div>
                    <div data-id="element-430">
                      <div className="text-white font-bold text-base md:text-lg" data-id="element-431">
                        {review.author}
                      </div>
                      <div className="text-gray-500 text-sm" data-id="element-432">{review.role}</div>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-20 md:py-32 relative" data-id="element-433">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-434">
          <div className="text-center mb-12 md:mb-20" data-id="element-435">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 md:mb-6" data-id="element-436">
              Frequently Asked Questions
            </h2>
            <p className="text-lg md:text-xl text-gray-400" data-id="element-437">
              Find answers to common questions about KDX
            </p>
          </div>

          <div className="space-y-4" data-id="element-438">
            {[{
            q: 'What is KDX?',
            a: 'KDX is an all-in-one digital marketplace for gaming top-ups, gift cards, utility payments, and everyday digital purchases. We make it easy to buy gaming top-ups, pay bills, and shop gadgets all in one secure app.'
          }, {
            q: 'How do I download KDX?',
            a: "KDX is available on both the Apple App Store and Google Play Store. Simply search for 'KDX' or click the download buttons on this page to get started."
          }, {
            q: 'How fast are transactions?',
            a: 'Most digital transactions (game top-ups, gift cards, airtime) are delivered instantly - usually within 3 seconds. Physical products take 2-5 business days for delivery.'
          }, {
            q: 'Is KDX secure?',
            a: 'Yes! We use bank-grade encryption and certified payment gateways to protect your transactions. Your financial information is never stored on our servers.'
          }, {
            q: 'How does the wallet work?',
            a: 'You can fund your KDX wallet using cards, bank transfers, or mobile money. Wallet funds can be used for faster checkout and exclusive deals.'
          }, {
            q: 'Can I get refunds?',
            a: 'Refunds are available for failed transactions or undelivered items. Digital goods that have been successfully delivered and redeemed are non-refundable per our terms.'
          }, {
            q: 'How do rewards work?',
            a: 'Earn points on eligible purchases, get referral bonuses, and enjoy seasonal promotions. Points can be redeemed for discounts on future purchases.'
          }].map((faq, i) => <GlassCard key={i} className="p-0 overflow-hidden border-white/10" hover={false} data-id="element-439">
                <button className="w-full text-left p-5 md:p-6 flex items-center justify-between focus:outline-none" onClick={() => setOpenFaq(openFaq === i ? null : i)} data-id="element-440">
                  <span className="font-semibold text-white text-base md:text-lg pr-8" data-id="element-441">
                    {faq.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 md:w-6 md:h-6 text-purple-400 transition-transform duration-300 shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-442" />
                </button>
                <AnimatePresence data-id="element-443">
                  {openFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-444">
                      <div className="px-5 md:px-6 pb-5 md:pb-6 text-gray-400 leading-relaxed border-t border-white/5 pt-4 text-sm md:text-base" data-id="element-445">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </GlassCard>)}
          </div>

          <div className="text-center mt-10 md:mt-12" data-id="element-446">
            <Link to="/faq" data-id="element-447">
              <Button variant="outline" className="text-base md:text-lg px-6 md:px-8 py-3 md:py-4" data-id="element-448">
                View All FAQs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* DOWNLOAD CTA */}
      <section id="download" className="py-20 md:py-32 relative overflow-hidden" data-id="element-449">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-purple-600/10 to-transparent" data-id="element-450"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-500/20 blur-[150px] rounded-full" data-id="element-451"></div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-452">
          <GlassCard className="p-10 md:p-16 lg:p-20 text-center border-purple-500/30 shadow-[0_0_80px_rgba(139,92,246,0.2)] bg-gradient-to-br from-purple-900/30 via-transparent to-blue-900/30" data-id="element-453">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-6 md:mb-8" data-id="element-454">
              Start Using KDX Today
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 md:mb-16 max-w-3xl mx-auto leading-relaxed" data-id="element-455">
              Download KDX and enjoy fast gaming top-ups, gift cards, digital
              payments, and secure wallet transactions.
            </p>

            <div className="flex flex-col lg:flex-row items-center justify-center gap-8 md:gap-12" data-id="element-456">
              <div className="flex flex-col sm:flex-row gap-4 md:gap-6" data-id="element-457">
                <AppBadge platform="ios" data-id="element-458" />
                <AppBadge platform="android" data-id="element-459" />
              </div>

              <div className="hidden lg:flex items-center gap-6 md:gap-8 border-l border-white/20 pl-8 md:pl-12" data-id="element-460">
                <div className="w-28 h-28 md:w-32 md:h-32 bg-white p-3 rounded-2xl flex items-center justify-center shadow-xl" data-id="element-461">
                  <QrCode className="w-full h-full text-black" data-id="element-462" />
                </div>
                <div className="text-left" data-id="element-463">
                  <div className="text-white font-bold text-lg md:text-xl mb-2" data-id="element-464">
                    Scan to Download
                  </div>
                  <div className="text-sm md:text-base text-gray-400" data-id="element-465">
                    Available on
                    <br data-id="element-466" />
                    iPhone and Android
                  </div>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      </section>

      {/* LEGAL & TRUST SECTION */}
      <section className="py-16 md:py-24 border-t border-white/10 bg-[#020408]" data-id="element-467">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-468">
          <div className="text-center mb-12 md:mb-16" data-id="element-469">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 md:mb-4" data-id="element-470">
              Legal, Privacy & User Protection
            </h2>
            <p className="text-base md:text-lg text-gray-400" data-id="element-471">
              We take your privacy and security seriously
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-4" data-id="element-472">
            {[{
            name: 'Terms & Conditions',
            icon: FileText,
            content: <div className="space-y-6" data-id="element-473">
                    <p className="text-gray-300" data-id="element-474">
                      These Terms and Conditions govern your use of KDX. By
                      accessing or using KDX, you agree to be bound by these
                      terms.
                    </p>
                    <div data-id="element-475">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-476">
                        Eligibility
                      </h4>
                      <p className="text-gray-300" data-id="element-477">
                        You must be at least 18 years old and capable of
                        entering into legally binding agreements.
                      </p>
                    </div>
                    <div data-id="element-478">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-479">
                        Account Security
                      </h4>
                      <p className="text-gray-300" data-id="element-480">
                        You are responsible for maintaining the confidentiality
                        of your account credentials.
                      </p>
                    </div>
                    <div data-id="element-481">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-482">
                        Payments & Processing
                      </h4>
                      <p className="text-gray-300" data-id="element-483">
                        All payments are processed through secure third-party
                        secure payment processors.
                      </p>
                    </div>
                    <div data-id="element-484">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-485">
                        Refund Policy
                      </h4>
                      <p className="text-gray-300" data-id="element-486">
                        Refunds are only applicable for failed, incomplete, or
                        duplicate transactions.
                      </p>
                    </div>
                    <Link to="/terms" className="inline-block text-purple-400 hover:underline mt-4" data-id="element-487">
                      Read full Terms & Conditions →
                    </Link>
                  </div>
          }, {
            name: 'Privacy Policy',
            icon: Shield,
            content: <div className="space-y-6" data-id="element-488">
                    <p className="text-gray-300" data-id="element-489">
                      KDX is committed to protecting your personal data.
                    </p>
                    <div data-id="element-490">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-491">
                        Information Collected
                      </h4>
                      <p className="text-gray-300" data-id="element-492">
                        Personal details, transaction data, and device usage
                        data.
                      </p>
                    </div>
                    <div data-id="element-493">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-494">
                        Data Sharing
                      </h4>
                      <p className="text-gray-300" data-id="element-495">
                        Your data may be shared with payment processors like
                        payment processors and regulatory authorities where
                        required.
                      </p>
                    </div>
                    <div data-id="element-496">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-497">
                        User Rights
                      </h4>
                      <p className="text-gray-300" data-id="element-498">
                        You have the right to access, correct, or request
                        deletion of your data.
                      </p>
                    </div>
                    <Link to="/privacy" className="inline-block text-purple-400 hover:underline mt-4" data-id="element-499">
                      Read full Privacy Policy →
                    </Link>
                  </div>
          }, {
            name: 'Cookie Policy',
            icon: Cookie,
            content: <div className="space-y-6" data-id="element-500">
                    <p className="text-gray-300" data-id="element-501">
                      KDX uses cookies to enhance user experience.
                    </p>
                    <div data-id="element-502">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-503">
                        Types of Cookies
                      </h4>
                      <ul className="list-disc list-inside text-gray-300 space-y-1" data-id="element-504">
                        <li data-id="element-505">Essential cookies for functionality</li>
                        <li data-id="element-506">Analytics cookies for performance tracking</li>
                        <li data-id="element-507">Preference cookies for personalization</li>
                      </ul>
                    </div>
                    <div data-id="element-508">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-509">
                        User Control
                      </h4>
                      <p className="text-gray-300" data-id="element-510">
                        Users can manage cookie preferences through browser
                        settings.
                      </p>
                    </div>
                    <Link to="/cookies" className="inline-block text-purple-400 hover:underline mt-4" data-id="element-511">
                      Read full Cookie Policy →
                    </Link>
                  </div>
          }, {
            name: 'Legal Terms of Use',
            icon: FileText,
            content: <div className="space-y-6" data-id="element-512">
                    <p className="text-gray-300" data-id="element-513">
                      This document governs your access to and use of the KDX
                      platform.
                    </p>
                    <div data-id="element-514">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-515">
                        Permitted Use
                      </h4>
                      <p className="text-gray-300" data-id="element-516">
                        You agree to use KDX only for lawful and authorized
                        purposes.
                      </p>
                    </div>
                    <div data-id="element-517">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-518">
                        Restrictions
                      </h4>
                      <p className="text-gray-300" data-id="element-519">
                        You must not reverse engineer, use bots, or attempt to
                        bypass security mechanisms.
                      </p>
                    </div>
                    <div data-id="element-520">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-521">
                        Intellectual Property
                      </h4>
                      <p className="text-gray-300" data-id="element-522">
                        All content, branding, and system design belong to KDX
                        and are protected by law.
                      </p>
                    </div>
                    <Link to="/legal-terms" className="inline-block text-purple-400 hover:underline mt-4" data-id="element-523">
                      Read full Legal Terms →
                    </Link>
                  </div>
          }, {
            name: 'Code of Conduct',
            icon: AlertTriangle,
            content: <div className="space-y-6" data-id="element-524">
                    <div data-id="element-525">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-526">
                        User Responsibilities
                      </h4>
                      <ul className="list-disc list-inside text-gray-300 space-y-1" data-id="element-527">
                        <li data-id="element-528">Act in good faith</li>
                        <li data-id="element-529">Avoid fraudulent activities</li>
                        <li data-id="element-530">Respect platform rules</li>
                        <li data-id="element-531">Not abuse promotions or rewards</li>
                      </ul>
                    </div>
                    <div data-id="element-532">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-533">
                        Consequences
                      </h4>
                      <p className="text-gray-300" data-id="element-534">
                        Violations may result in suspension or permanent bans.
                      </p>
                    </div>
                    <Link to="/code-of-conduct" className="inline-block text-purple-400 hover:underline mt-4" data-id="element-535">
                      Read full Code of Conduct →
                    </Link>
                  </div>
          }, {
            name: 'Your Privacy Choices',
            icon: UserCog,
            content: <div className="space-y-6" data-id="element-536">
                    <div data-id="element-537">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-538">
                        Privacy Controls
                      </h4>
                      <ul className="list-disc list-inside text-gray-300 space-y-1" data-id="element-539">
                        <li data-id="element-540">Marketing communications</li>
                        <li data-id="element-541">Data tracking preferences</li>
                        <li data-id="element-542">Account data access</li>
                      </ul>
                    </div>
                    <div data-id="element-543">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-544">
                        Data Requests
                      </h4>
                      <p className="text-gray-300" data-id="element-545">
                        Requests for data access or deletion can be submitted
                        through support.
                      </p>
                    </div>
                    <Link to="/privacy-choices" className="inline-block text-purple-400 hover:underline mt-4" data-id="element-546">
                      Read full Privacy Choices →
                    </Link>
                  </div>
          }, {
            name: 'Rules and Rewards',
            icon: Award,
            content: <div className="space-y-6" data-id="element-547">
                    <p className="text-gray-300" data-id="element-548">
                      KDX may offer promotional incentives.
                    </p>
                    <div data-id="element-549">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-550">
                        Conditions
                      </h4>
                      <ul className="list-disc list-inside text-gray-300 space-y-1" data-id="element-551">
                        <li data-id="element-552">Subject to eligibility and verification</li>
                        <li data-id="element-553">May have expiration dates</li>
                        <li data-id="element-554">Cannot be abused or manipulated</li>
                      </ul>
                    </div>
                    <div data-id="element-555">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-556">
                        Platform Discretion
                      </h4>
                      <p className="text-gray-300" data-id="element-557">
                        KDX reserves full discretion to modify or withdraw
                        rewards.
                      </p>
                    </div>
                    <Link to="/rewards-rules" className="inline-block text-purple-400 hover:underline mt-4" data-id="element-558">
                      Read full Rewards Rules →
                    </Link>
                  </div>
          }, {
            name: 'Support Policy',
            icon: HelpCircle,
            content: <div className="space-y-6" data-id="element-559">
                    <p className="text-gray-300" data-id="element-560">
                      KDX provides customer support for transaction issues,
                      wallet inquiries, refund requests, and account access.
                    </p>
                    <div data-id="element-561">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-562">
                        Response Time
                      </h4>
                      <p className="text-gray-300" data-id="element-563">
                        Support requests are handled within a reasonable
                        timeframe.
                      </p>
                    </div>
                    <div data-id="element-564">
                      <h4 className="text-lg font-bold text-white mb-2" data-id="element-565">
                        Contact Support
                      </h4>
                      <p className="text-gray-300" data-id="element-566">
                        Email:{' '}
                        <a href="mailto:kdx.limited.ng@gmail.com" className="text-purple-400 hover:underline" data-id="element-567">
                          kdx.limited.ng@gmail.com
                        </a>
                      </p>
                    </div>
                    <Link to="/support" className="inline-block text-purple-400 hover:underline mt-4" data-id="element-568">
                      Visit Support Center →
                    </Link>
                  </div>
          }].map((item, i) => {
            const isOpen = openFaq === 1000 + i;
            return <GlassCard key={i} className="p-0 overflow-hidden border-white/10" hover={false} data-id="element-569">
                  <button className="w-full text-left p-6 flex items-center justify-between focus:outline-none hover:bg-white/[0.02] transition-colors" onClick={() => setOpenFaq(isOpen ? null : 1000 + i)} data-id="element-570">
                    <div className="flex items-center gap-4" data-id="element-571">
                      <item.icon className="w-6 h-6 text-purple-400 shrink-0" data-id="element-572" />
                      <span className="font-semibold text-white text-base md:text-lg" data-id="element-573">
                        {item.name}
                      </span>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-purple-400 transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-574" />
                  </button>
                  <AnimatePresence data-id="element-575">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-576">
                        <div className="px-6 pb-6 border-t border-white/5 pt-6" data-id="element-577">
                          {item.content}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </GlassCard>;
          })}
          </div>
        </div>
      </section>
    </div>;
};